import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { newAttemptNonce } from '../../lib/attempt'
import { seatOverride } from '../../lib/seat'
import { useLocale } from '../../lib/i18n'
import { useSessionFeed } from '../../lib/useSessionFeed'
import { hhmm, useFictionalClock } from '../../lib/useFictionalClock'
import type { Opening, SessionState } from '../../lib/contract'
import { findScenarioByPack, roleOf, type Scenario } from '../../content/scenarios'
import type { TrafficRow } from './parts/Traffic'
import type { Plan } from './parts/Instruments'

/**
 * Everything the desk KNOWS. Nothing about what it shows.
 *
 * The Correspondent loop is CONTACT + PASS: you talk on the wire, you write
 * down what was agreed, and when there is nothing more to say you jump the
 * clock. The World pass resolves the stretch he lived through, and the next
 * contact opens with his own report of it.
 *
 * The desk's timers are the OPERATOR'S OWN — client-only state the engine never
 * parses. That is why they can be wrong, and why the turnaround meter can read
 * OVERDUE while he is perfectly fine.
 *
 * This hook is DEVICE-BLIND on purpose: it is the seam that lets a phone
 * surface and a desktop surface be genuinely different UIs over identical
 * logic, instead of one component tree fighting breakpoints.
 */

const DEFAULT_PC = 'a person, here as themselves'
/** If a jump lands on nothing, say so rather than filling the silence. */
const SILENCE_MS = 22000

export interface DeskSession {
  // identity
  packId: string
  scenario: Scenario | undefined
  station: string
  role: string | undefined

  // lifecycle
  sessionId: string | null
  starting: boolean
  error: string | null
  begin: () => void
  /**
   * True when the surface was entered through a choice already made (a door),
   * so it opens itself rather than asking again. The surface uses this to show
   * an opening state instead of a threshold.
   */
  autoBegin: boolean

  /**
   * THE OPENING RUN (brief §6.4). The pack's authored cards, or null when this
   * pack has no run — in which case the desk boots exactly as it did before.
   * Present after the run has been played too: the cards are the head of the
   * dossier's given set from then on (four-doors §5.8 law 3), so they are kept
   * rather than consumed.
   */
  opening: Opening | null
  /** True while the run should be on screen instead of the desk. */
  openingRunning: boolean
  /**
   * The opening question has been answered. Before this, the surface shows
   * neither the run nor the desk — mounting the desk first and swapping to the
   * run a moment later is the flash a naive default produces.
   */
  openingSettled: boolean
  /** The run's closing action fired — mount the desk. */
  finishOpening: () => void

  // the channel
  rows: TrafficRow[]
  working: boolean
  terminated: boolean
  terminationReason: string | null

  // the clock and the book
  now: Date | null
  arrangement: string | null
  nextCall: Date | null
  setNextCall: (d: Date | null) => void
  plan: Plan | null
  setPlan: (p: Plan | null) => void
  skedLabel: string

  // acts
  send: (text: string) => void
  arrange: (text: string | null) => void
  jump: () => void
  signOff: () => void
  jumpBusy: boolean
  skedBusy: boolean
}

export function useDeskSession(
  packIdOverride?: string,
  autoBegin = false,
  /** ⚠ The price the player agreed to at the threshold, in micros. See `Threshold.tsx`. */
  agreedPriceMicros?: number,
): DeskSession {
  const { locale } = useLocale()
  const routeParams = useParams()
  const packId = packIdOverride ?? routeParams.packId ?? ''
  const [params, setParams] = useSearchParams()
  const scenario = findScenarioByPack(packId)

  const [sessionId, setSessionId] = useState<string | null>(params.get('session'))
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [rows, setRows] = useState<TrafficRow[]>([])
  const [working, setWorking] = useState(false)
  const [state, setState] = useState<SessionState | null>(null)
  const [restHour, setRestHour] = useState(19)

  /**
   * THE OPENING RUN (brief §6.4). Fetched once at desk mount, create and resume
   * alike, because `complete` is the SERVER's skip rule (derived from the ledger,
   * never stored) — the client is not asked to remember whether this session has
   * been played, which is what makes a resume land straight on the desk.
   *
   * `runOpening` is the on-screen flag and starts false: until the fetch answers,
   * neither the run nor the desk is the right thing to show, and flashing one
   * before the other is the failure mode a naive default produces.
   */
  const [opening, setOpening] = useState<Opening | null>(null)
  const [runOpening, setRunOpening] = useState(false)
  /** The fetch has answered (or failed). Until then the surface shows neither. */
  const [openingSettled, setOpeningSettled] = useState(false)
  /** Which session we asked for — so a NEW session asks again. */
  const openingAskedFor = useRef<string | null>(null)
  /** Which session's history has been pulled. Same reason, same shape. */
  const hydratedFor = useRef<string | null>(null)
  /** Every message id already on the surface, from either source. */
  const seenMsgIds = useRef<Set<string>>(new Set())

  const { now, anchor } = useFictionalClock()

  // The operator's book. Deliberately client-only state.
  const [nextCall, setNextCall] = useState<Date | null>(null)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [jumpBusy, setJumpBusy] = useState(false)
  const [skedBusy, setSkedBusy] = useState(false)

  const startGuard = useRef(false)
  const rxSeen = useRef(0)
  /** Outbound traffic already echoed locally, awaiting its engine echo. */
  const pendingOut = useRef<string[]>([])
  /** Set by the feed's generation event; the next arriving line opens a burst. */
  const nextIsBurst = useRef(false)
  const silenceTimer = useRef<number | null>(null)
  const seq = useRef(0)
  /** One open at a time — see `begin`. Written synchronously, unlike `starting`. */
  const openInFlight = useRef(false)
  /** The purchase key for the sitting being opened. Spent by a success, kept across failures. */
  const attemptKey = useRef<string>('')

  const addRow = useCallback((row: Omit<TrafficRow, 'id'>) => {
    seq.current += 1
    setRows((prev) => [...prev, { ...row, id: `r${seq.current}` }].slice(-300))
  }, [])

  const seam = useCallback((text: string) => addRow({ kind: 'seam', text }), [addRow])

  // ---------------------------------------------------------------- start

  const begin = useCallback(async () => {
    // ⚠ ONE OPEN AT A TIME, synchronously. The reasoning is the same as the thread's and the
    // inhabit surface's: `requestId` was the constant `desk:${packId}`, so every sitting after the
    // first reused the first one's entitlement (`lib/attempt.ts`). A per-attempt nonce fixes that
    // and gives up the double-tap protection the constant was accidentally providing, so this ref
    // has to provide it deliberately — `starting` cannot, being state.
    if (openInFlight.current) return
    openInFlight.current = true
    setStarting(true)
    setError(null)
    // ⚠ Kept across a failure, spent by a success — see the same guard in `useInhabitSession`. A
    // fresh key per press would buy a second pack-start when a retry follows a hold that was
    // already taken.
    if (!attemptKey.current) attemptKey.current = newAttemptNonce()
    try {
      const res = await api.startSession({
        packId,
        playerCharacterContent: params.get('pc') ?? DEFAULT_PC,
        strategy: 'PerceptionFirst',
        // See the note in useMessagingSession: the locale is fixed at session creation
        // (language design §5). The desk carries it for the same reason the thread does.
        language: locale,
        agreedPriceMicros,
        requestId: `desk:${packId}:${attemptKey.current}`,
        // The player's clock, for a pack that runs on it (2026-09-11).
        utcOffsetMinutes: -new Date().getTimezoneOffset(),
        ...seatOverride(),
      })
      attemptKey.current = ''
      setSessionId(res.sessionId)
      anchor(res.fictionalTime)
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('session', res.sessionId)
          return next
        },
        { replace: true },
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The desk could not open.')
    } finally {
      setStarting(false)
      // Released on both paths — the guard covers the in-flight window, not the session's life.
      openInFlight.current = false
    }
  }, [packId, params, setParams, anchor])

  /*
   * OPEN ON ARRIVAL when the choice was already made at the door.
   *
   * ⚠ THIS SPENDS MONEY, so the guard is not optional: React StrictMode mounts,
   * unmounts and remounts in development, and an unguarded effect would start —
   * and bill — two sessions. A ref survives that simulated remount; a state flag
   * would not.
   *
   * It also never RETRIES. If the start fails, the surface shows the error and a
   * button; an effect that retried on its own could bill in a loop against a
   * server that is failing for some other reason.
   */
  const autoBeginGuard = useRef(false)

  useEffect(() => {
    if (!autoBegin || autoBeginGuard.current) return
    if (sessionId || starting || error) return
    autoBeginGuard.current = true
    void begin()
  }, [autoBegin, sessionId, starting, error, begin])

  useEffect(() => {
    if (!sessionId || startGuard.current) return
    startGuard.current = true
    void api
      .getState(sessionId)
      .then((s) => {
        setState(s)
        anchor(s.fictionalTime)
      })
      .catch(() => {
        // Sessions live in memory; a restart drops them from the registry.
        setSessionId(null)
        startGuard.current = false
      })
  }, [sessionId, anchor])

  /*
   * The opening run, asked once per session id. A pack with no run answers
   * `opening: null` and the desk mounts exactly as it did before this existed
   * (graceful absence, dumb-client law 3) — and a failed call does the same,
   * because a desk that will not open is a worse outcome than a missed intro.
   */
  useEffect(() => {
    if (!sessionId || openingAskedFor.current === sessionId) return
    openingAskedFor.current = sessionId
    setOpeningSettled(false)
    void api
      .getOpening(sessionId)
      .then((r) => {
        setOpening(r.opening)
        // `complete` is absent (not false) on the no-opening path — see the
        // contract's note. Gating on `opening != null` first makes that moot.
        setRunOpening(r.opening != null && !r.complete)
      })
      .catch(() => {
        setOpening(null)
        setRunOpening(false)
      })
      .finally(() => setOpeningSettled(true))
  }, [sessionId])

  /*
   * THE BACKLOG (brief §2.2 — "backlog is instant").
   *
   * History is hydrated once per session from /messages, which is the ONE source
   * of it: the opening run's pre-traffic is ordinary history and arrives here
   * like everything else, rather than through a second delivery path of its own
   * (§6.4). Every hydrated row carries revealMs 0 — pacing applies only to lines
   * arriving NOW, so a reload or a re-entry never re-paces what has been read.
   *
   * The live feed is subscribed on the same session, so the two can overlap at
   * the seam; `msgId` is what makes that overlap a no-op rather than a doubled
   * line, and it is why hydration MERGES rather than replaces.
   */
  useEffect(() => {
    if (!sessionId || hydratedFor.current === sessionId) return
    hydratedFor.current = sessionId
    void api
      .getMessages(sessionId)
      .then((r) => {
        const history = r.messages
          .filter((m) => m.content && !m.content.startsWith('//'))
          .map((m, i) => ({
            id: `h${i}`,
            msgId: m.messageId,
            kind: (m.senderKind === 'Player' ? 'tx' : 'rx') as 'tx' | 'rx',
            text: m.content,
            register: m.registerClass,
            channel: m.channelId,
            at: m.fictionalTimestamp ? new Date(m.fictionalTimestamp) : null,
            revealMs: 0,
          }))
        for (const h of history) if (h.msgId) seenMsgIds.current.add(h.msgId)
        setRows((prev) => {
          const seen = new Set(history.map((h) => h.msgId))
          return [...history, ...prev.filter((r2) => !r2.msgId || !seen.has(r2.msgId))]
        })
      })
      .catch(() => {
        /* No history is a survivable state; a desk that will not open is not. */
      })
  }, [sessionId])

  // The sked hour is the pack's, not the desk's.
  useEffect(() => {
    void api
      .listPacks()
      .then((r) => {
        const card = r.packs.find((p) => p.id === packId)
        if (card?.restMorningHour != null) setRestHour(card.restMorningHour)
      })
      .catch(() => {})
  }, [packId])

  // ---------------------------------------------------------------- traffic

  useSessionFeed(sessionId, {
    message: (m) => {
      if (!m.content) return
      setWorking(false)
      anchor(m.fictionalTimestamp)

      // NOTE: every perception line arrives as senderKind=System on the
      // 'perception' channel — that is HIS voice, not a system notice. The only
      // true seam is the advance's marker, which the desk itself authored and
      // prefixed with '//'. Keying on senderKind here would silence him.
      if (m.content.startsWith('//')) {
        seam(m.content.replace(/^\/\/\s*/, ''))
        return
      }

      if (m.senderKind === 'Player') {
        // The desk echoed this locally the moment it was keyed, so the engine's
        // echo SETTLES that row rather than adding a second one: TX state
        // clears and the line takes the engine's stamp (brief §2.4).
        const i = pendingOut.current.indexOf(m.content)
        if (i !== -1) {
          pendingOut.current.splice(i, 1)
          setRows((prev) => {
            const idx = prev.findIndex((r) => r.pending && r.text === m.content)
            if (idx === -1) return prev
            const next = [...prev]
            next[idx] = {
              ...next[idx],
              pending: false,
              at: m.fictionalTimestamp ? new Date(m.fictionalTimestamp) : next[idx].at,
            }
            return next
          })
          return
        }
        addRow({
          kind: 'tx',
          text: m.content,
          at: m.fictionalTimestamp ? new Date(m.fictionalTimestamp) : null,
        })
        return
      }

      // Already in the backlog: the hydration and the live feed overlap at the
      // seam, and the same line must not land twice. A REF, not `rows` — the
      // handler closes over its render's state, and a stale copy would let
      // duplicates through exactly when the overlap is largest.
      if (m.messageId) {
        if (seenMsgIds.current.has(m.messageId)) return
        seenMsgIds.current.add(m.messageId)
      }

      rxSeen.current += 1
      addRow({
        msgId: m.messageId,
        kind: 'rx',
        text: m.content,
        // The register, verbatim from the server — the surface styles off this,
        // never off the words. Absent on engines older than the field, in which
        // case the row falls back to `kind`.
        register: m.registerClass,
        // A separate axis: `body` is not speech and must not read as something
        // he said, even though it classifies as Voice.
        channel: m.channelId,
        // One generation, one burst: the feed's own generation event marks the
        // boundary, so the client never infers it from arrival timing.
        burstStart: nextIsBurst.current,
        at: m.fictionalTimestamp ? new Date(m.fictionalTimestamp) : null,
        revealMs: m.revealDurationMs ?? 0,
      })
      nextIsBurst.current = false
    },
    'chunk-gen-started': () => {
      setWorking(true)
      nextIsBurst.current = true
    },
    error: (e) => setError(e.message),
  })

  // Poll for the arrangement and termination. The traffic is the live channel;
  // this is only the book.
  useEffect(() => {
    if (!sessionId) return
    let alive = true
    const tick = async () => {
      try {
        const s = await api.getState(sessionId)
        if (!alive) return
        setState(s)
        anchor(s.fictionalTime)
      } catch {
        /* transient */
      }
    }
    void tick()
    const id = window.setInterval(tick, 8000)
    return () => {
      alive = false
      window.clearInterval(id)
    }
  }, [sessionId, anchor])

  useEffect(
    () => () => {
      if (silenceTimer.current) window.clearTimeout(silenceTimer.current)
    },
    [],
  )

  /** A jump that lands on nothing is a real state, not a failure. */
  const watchForSilence = useCallback(
    (landed: Date) => {
      if (silenceTimer.current) window.clearTimeout(silenceTimer.current)
      const before = rxSeen.current
      silenceTimer.current = window.setTimeout(() => {
        if (rxSeen.current === before) {
          seam(`${hhmm(landed)} · nothing on the channel`)
        }
      }, SILENCE_MS)
    },
    [seam],
  )

  // ---------------------------------------------------------------- acts

  const send = useCallback(
    async (text: string) => {
      if (!sessionId) return
      pendingOut.current.push(text)
      // Shown as typed and marked TX until the engine acknowledges it — the
      // will-record, in the operator's own words (brief §2.4).
      addRow({ kind: 'tx', text, at: now, pending: true })
      setWorking(true)
      try {
        await api.sendInput(sessionId, text)
      } catch (e) {
        setWorking(false)
        setError(e instanceof Error ? e.message : 'That did not go out.')
      }
    },
    [sessionId, addRow, now],
  )

  const arrange = useCallback(
    async (text: string | null) => {
      if (!sessionId) return
      try {
        await api.setIntent(sessionId, text)
        setState((s) => (s ? { ...s, standingIntent: text } : s))
      } catch {
        /* the arrangement is an aid; a failure must not break the desk */
      }
    },
    [sessionId],
  )

  /** Whatever the desk wrote down as the next contact rides with every advance. */
  const arrangedISO = useCallback(() => {
    if (!nextCall) return null
    const d = new Date(nextCall.getTime())
    d.setUTCSeconds(0, 0)
    return d.toISOString()
  }, [nextCall])

  const jump = useCallback(async () => {
    if (!sessionId || !nextCall || jumpBusy) return
    const before = now
    setJumpBusy(true)
    try {
      const r = await api.advance(sessionId, {
        fictionalTime: nextCall.toISOString(),
        marker: '// the desk stood by',
        arrangedContactAt: arrangedISO(),
      })
      const landed = r.coveredUntil ? new Date(r.coveredUntil) : nextCall
      anchor(landed)

      if (r.interrupted) {
        // The skip yielded to the signal: the leg resolved only as far as the
        // moment he came onto the channel himself.
        seam('traffic, early')
      } else if (before) {
        const mins = Math.max(
          1,
          Math.round((landed.getTime() - before.getTime()) / 60000),
        )
        seam(`${mins} minutes pass at the desk`)
      }
      setNextCall(null)
      watchForSilence(landed)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The clock would not move.')
    } finally {
      setJumpBusy(false)
    }
  }, [sessionId, nextCall, jumpBusy, now, arrangedISO, anchor, seam, watchForSilence])

  const signOff = useCallback(async () => {
    if (!sessionId || skedBusy) return
    setSkedBusy(true)
    try {
      const s = await api.getState(sessionId)
      const cur = new Date(s.fictionalTime)
      const target = new Date(cur)
      target.setUTCHours(restHour, 30, 0, 0)
      if (cur.getUTCHours() >= restHour || target <= cur) {
        target.setUTCDate(target.getUTCDate() + 1)
      }
      const r = await api.advance(sessionId, {
        fictionalTime: target.toISOString(),
        marker: `// signed off — next sked ${hhmm(target)}`,
        arrangedContactAt: arrangedISO(),
      })
      const landed = r.coveredUntil ? new Date(r.coveredUntil) : target
      anchor(landed)
      if (r.interrupted) seam('traffic, early')
      setNextCall(null)
      watchForSilence(landed)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The clock would not move.')
    } finally {
      setSkedBusy(false)
    }
  }, [sessionId, skedBusy, restHour, arrangedISO, anchor, seam, watchForSilence])

  return {
    packId,
    scenario,
    station: scenario?.title ?? packId,
    role: scenario ? roleOf(scenario) : undefined,

    sessionId,
    starting,
    error,
    begin: () => void begin(),
    autoBegin,

    opening,
    openingRunning: runOpening,
    openingSettled,
    // Ending the run is a CLIENT act only: the server's own `complete` turns
    // true by itself the moment the player keys their first line (OpeningGate
    // reads the ledger), so there is nothing to tell it and nothing to store.
    finishOpening: () => setRunOpening(false),

    rows,
    working,
    terminated: state?.terminated ?? false,
    terminationReason: state?.terminationReason ?? null,

    now,
    arrangement: state?.standingIntent ?? null,
    nextCall,
    setNextCall,
    plan,
    setPlan,
    // The pack's own rest hour, so the label cannot claim an evening when the
    // pack sleeps until morning (Silfurberg: 08:30).
    skedLabel:
      restHour < 12
        ? '— sign off until the morning sked —'
        : '— sign off until the evening sked —',

    send: (t: string) => void send(t),
    arrange: (t: string | null) => void arrange(t),
    jump: () => void jump(),
    signOff: () => void signOff(),
    jumpBusy,
    skedBusy,
  }
}
